import React, { useCallback, useEffect, useState } from 'react';
import { FileSignature, Loader2, RefreshCw, Send } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch } from './MedicationExtras';

const STATUS = {
  sent: ['Sent', 'bg-blue-100 text-blue-800'],
  viewed: ['Viewed', 'bg-amber-100 text-amber-800'],
  signed: ['Signed', 'bg-green-100 text-green-800'],
  expired: ['Expired', 'bg-red-100 text-red-700'],
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : '');

const TrackerRow = ({ r, showPatient, onResend, busy }) => {
  const [label, cls] = STATUS[r.status] || STATUS.sent;
  return (
    <tr className="border-t align-top" data-testid={`esign-row-${r.id}`}>
      {showPatient && <td className="px-3 py-2 font-medium text-gray-800">{r.patientName}</td>}
      <td className="px-3 py-2">
        <p className="text-gray-800">{r.physicianName}</p>
        <p className="text-xs text-gray-500">{r.channel === 'fax' ? `Fax ${r.physicianFax} (demo)` : r.physicianEmail}</p>
      </td>
      <td className="px-3 py-2 text-gray-700">
        {fmt(r.createdAt)}
        {(r.resendCount > 0 || r.remindersSent > 0) && (
          <p className="text-xs text-gray-500">{r.resendCount || 0} resent, {r.remindersSent || 0} reminders</p>
        )}
      </td>
      <td className="px-3 py-2">
        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`} data-testid={`esign-status-${r.id}`}>{label}</span>
        {r.status === 'signed' && <p className="text-xs text-gray-500 mt-1">{fmt(r.signedAt)} by {r.signedName}. Copy in Packages</p>}
        {r.status === 'viewed' && <p className="text-xs text-gray-500 mt-1">Opened {fmt(r.viewedAt)}</p>}
      </td>
      <td className="px-3 py-2 text-right">
        {r.status !== 'signed' && (
          <button onClick={() => onResend(r)} disabled={busy === r.id} data-testid={`esign-resend-${r.id}`}
            className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-gray-900 text-[#d4af37] hover:bg-gray-800 disabled:opacity-50">
            {busy === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Resend
          </button>
        )}
      </td>
    </tr>
  );
};

const CompactRow = ({ r, onResend, busy }) => {
  const [label, cls] = STATUS[r.status] || STATUS.sent;
  return (
    <li className="px-4 py-2.5" data-testid={`esign-row-${r.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium text-gray-800 truncate">{r.patientName}</p>
          <p className="text-xs text-gray-500 truncate">{r.physicianName}, sent {fmt(r.createdAt)}</p>
        </div>
        <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`} data-testid={`esign-status-${r.id}`}>{label}</span>
      </div>
      {r.status !== 'signed' && (
        <button onClick={() => onResend(r)} disabled={busy === r.id} data-testid={`esign-resend-${r.id}`}
          className="mt-1.5 inline-flex items-center gap-1 px-3 py-0.5 rounded-full text-xs font-medium bg-gray-900 text-[#d4af37] hover:bg-gray-800 disabled:opacity-50">
          {busy === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Resend
        </button>
      )}
    </li>
  );
};

export const SignatureTracker = ({ patientId, refreshKey = 0, compact = false }) => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    const res = await authFetch(`/esign/requests${patientId ? `?patientId=${patientId}` : ''}`);
    const data = await res.json().catch(() => ({}));
    setRows(data.requests || []);
  }, [patientId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const resend = async (r) => {
    setBusy(r.id);
    try {
      const res = await authFetch(`/esign/requests/${r.id}/resend`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Email service busy, please try again in a minute');
      toast.success(data.message);
      load();
    } catch (e) {
      toast.error(`Resend failed: ${e.message}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200" data-testid="signature-tracker">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2"><FileSignature className="w-5 h-5 text-[#b8941f]" /> 485 Signature Requests</h3>
        <button onClick={load} className="text-gray-500 hover:text-gray-900" data-testid="signature-tracker-refresh"><RefreshCw className="w-4 h-4" /></button>
      </div>
      {rows === null ? <Loader2 className="w-5 h-5 animate-spin m-4" /> : rows.length === 0 ? (
        <p className="p-4 text-sm text-gray-500" data-testid="signature-tracker-empty">No signature requests yet. Use "Send for Signature" on a generated 485.</p>
      ) : compact ? (
        <ul className="divide-y max-h-96 overflow-y-auto">
          {rows.slice(0, 15).map(r => <CompactRow key={r.id} r={r} onResend={resend} busy={busy} />)}
        </ul>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-left">
              <tr>
                {!patientId && <th className="px-3 py-2">Patient</th>}
                <th className="px-3 py-2">Physician</th><th className="px-3 py-2">Sent</th><th className="px-3 py-2">Status</th><th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>{rows.map(r => <TrackerRow key={r.id} r={r} showPatient={!patientId} onResend={resend} busy={busy} />)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};
